import React, { useEffect, useState, useRef } from 'react';
import { MessageSquare, Star, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_URL } from '../config/app';
import { GOOGLE_REVIEW_URL } from '../config/business';

interface GoogleReviewAuthor {
  displayName: string;
  photoUri: string;
}

interface GoogleReview {
  authorAttribution: GoogleReviewAuthor;
  rating: number;
  text: { text: string };
  relativePublishTimeDescription: string;
}

interface GoogleReviewsData {
  rating: number;
  userRatingCount: number;
  reviews: GoogleReview[];
}

const GoogleLogo: React.FC = () => (
  <div className="bg-white px-4 py-2 rounded-md flex items-center gap-2">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M44.5 20H24v8h11.7C34.1 33.1 29.5 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34 6.5 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.2-4z"
      />
      <path
        fill="#EA4335"
        d="M6.3 14.7l6.6 4.8C14.5 16 19 13 24 13c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34 6.5 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#FBBC05"
        d="M24 44c5.2 0 9.9-1.8 13.5-4.7l-6.2-5.2C29.5 35.5 26.9 36 24 36c-5.5 0-10.1-2.9-11.7-7.1l-6.6 5.1C9.5 39.6 16.3 44 24 44z"
      />
      <path
        fill="#34A853"
        d="M44.5 20H24v8h11.7c-.8 2.2-2.3 4.1-4.2 5.4l6.2 5.2C41.4 35.1 44.5 30 44.5 24c0-1.3-.1-2.7-.2-4z"
      />
    </svg>
    <span className="text-gray-800 font-bold text-base">Google</span>
  </div>
);

interface StarRatingProps {
  rating: number;
  size?: number;
}

const StarRating: React.FC<StarRatingProps> = ({ rating, size = 16 }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((index) => (
      <Star
        key={index}
        size={size}
        fill={index <= Math.round(rating) ? '#FBBC05' : 'transparent'}
        color="#FBBC05"
      />
    ))}
  </div>
);

interface ReviewCardSkeletonProps {
  count: number;
}

const ReviewCardSkeleton: React.FC<ReviewCardSkeletonProps> = ({ count }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="flex-shrink-0 w-72 bg-[#36393F] border border-white/10 rounded-xl p-4 animate-pulse"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-white/10" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 bg-white/10 rounded w-3/4" />
            <div className="h-3 bg-white/10 rounded w-1/2" />
          </div>
        </div>
        <div className="flex gap-0.5 mb-3">
          {[...Array(5)].map((_, j) => (
            <div key={j} className="w-4 h-4 bg-white/10 rounded" />
          ))}
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-white/10 rounded w-full" />
          <div className="h-3 bg-white/10 rounded w-5/6" />
          <div className="h-3 bg-white/10 rounded w-4/6" />
        </div>
      </div>
    ))}
  </>
);

interface ReviewCardProps {
  review: GoogleReview;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review }) => {
  const initial = review.authorAttribution.displayName.charAt(0).toUpperCase();

  return (
    <div className="w-full bg-[#36393F] border border-white/10 rounded-xl p-4 flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        {review.authorAttribution.photoUri ? (
          <img
            src={review.authorAttribution.photoUri}
            alt={review.authorAttribution.displayName}
            className="w-10 h-10 rounded-full object-cover"
            onError={(e) => {
              const target = e.currentTarget;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement | null;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className="w-10 h-10 rounded-full bg-[#4285F4]/30 text-[#4285F4] font-bold text-sm items-center justify-center flex-shrink-0"
          style={{ display: review.authorAttribution.photoUri ? 'none' : 'flex' }}
          aria-hidden="true"
        >
          {initial}
        </div>
        <div className="min-w-0">
          <p className="text-white text-sm font-medium truncate">
            {review.authorAttribution.displayName}
          </p>
          <p className="text-gray-400 text-xs">
            {review.relativePublishTimeDescription}
          </p>
        </div>
      </div>

      <StarRating rating={review.rating} size={14} />

      <p className="mt-2 text-gray-300 text-xs leading-relaxed line-clamp-3 flex-1">
        {review.text.text}
      </p>
    </div>
  );
};

const GoogleReviewsWidget: React.FC = () => {
  const [data, setData] = useState<GoogleReviewsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchReviews = async (): Promise<void> => {
      try {
        const response = await fetch(`${API_URL}/api/google-reviews`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json: GoogleReviewsData = await response.json();
        setData(json);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const reviews = data?.reviews ?? [];
  const hasReviews = reviews.length > 0;

  const scrollTo = (index: number) => {
    if (!carouselRef.current) return;
    const cards = carouselRef.current.querySelectorAll<HTMLElement>('[data-card]');
    if (cards[index]) {
      cards[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
    setActiveIndex(index);
  };

  const prev = () => scrollTo(Math.max(0, activeIndex - 1));
  const next = () => scrollTo(Math.min(reviews.length - 1, activeIndex + 1));

  return (
    <section className="py-16 bg-gradient-to-b from-[#2F3136] to-[#36393F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#2F3136] border border-white/5 rounded-2xl p-6 shadow-lg relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#4285F4] opacity-5 rounded-full blur-[50px]" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-[#4285F4] opacity-5 rounded-full blur-[80px]" />

          {/* Zone haute */}
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <div className="text-center md:text-left max-w-lg">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#4285F4]/20 text-[#4285F4] mb-4">
                <Star size={14} className="mr-1.5" fill="#4285F4" />
                <span className="text-xs font-medium">Avis Google</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">
                Votre avis nous aide à nous{' '}
                <span className="text-[#4285F4]">améliorer</span>
              </h2>
              <p className="text-gray-300 text-sm">
                Partagez votre expérience et aidez d'autres personnes à trouver
                une solution à leurs problèmes informatiques.
              </p>
            </div>

            <div className="flex flex-col items-center gap-4">
              <GoogleLogo />
              {loading ? (
                <div className="flex gap-0.5 animate-pulse">
                  {[...Array(5)].map((_, i) => <div key={i} className="w-5 h-5 bg-white/10 rounded" />)}
                </div>
              ) : data !== null ? (
                <div className="flex flex-col items-center gap-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-white text-3xl font-bold">{data.rating.toFixed(1).replace('.', ',')}</span>
                    <span className="text-gray-400 text-sm">/ 5</span>
                  </div>
                  <StarRating rating={data.rating} size={20} />
                  <span className="text-gray-400 text-xs">{data.userRatingCount} avis</span>
                </div>
              ) : (
                <StarRating rating={5} size={20} />
              )}
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-4 py-2 rounded-md font-medium text-white bg-[#4285F4] hover:bg-[#3367D6] transition-colors focus:outline-none focus:ring-2 focus:ring-[#4285F4] focus:ring-offset-2 focus:ring-offset-[#2F3136]"
              >
                <MessageSquare size={16} className="mr-2" aria-hidden="true" />
                <span>Laisser un avis Google</span>
                <ExternalLink size={14} className="ml-2" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Zone basse — Carousel */}
          {error && (
            <p className="relative z-10 text-center text-gray-500 text-xs py-2">
              Avis temporairement indisponibles
            </p>
          )}

          {!error && (loading || hasReviews) && (
            <div className="relative z-10">
              {/* Carousel */}
              <div
                ref={carouselRef}
                className="flex gap-4 overflow-x-hidden scroll-smooth items-stretch"
              >
                {loading ? (
                  <ReviewCardSkeleton count={4} />
                ) : (
                  reviews.map((review, index) => (
                    <div key={index} data-card className="flex-shrink-0 w-72 flex">
                      <ReviewCard review={review} />
                    </div>
                  ))
                )}
              </div>

              {/* Contrôles */}
              {!loading && reviews.length > 1 && (
                <div className="flex items-center justify-center gap-4 mt-6">
                  <button
                    onClick={prev}
                    disabled={activeIndex === 0}
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft size={16} className="text-white" />
                  </button>

                  {/* Dots */}
                  <div className="flex gap-2">
                    {reviews.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => scrollTo(i)}
                        className={`rounded-full transition-all duration-300 ${
                          i === activeIndex
                            ? 'w-6 h-2 bg-[#4285F4]'
                            : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                        }`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={next}
                    disabled={activeIndex === reviews.length - 1}
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                  >
                    <ChevronRight size={16} className="text-white" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsWidget;
